import * as _ from 'lodash-es';
import { metrics, type Attributes, type Counter, type Histogram } from '@opentelemetry/api';
import { type AnyValueMap } from '@opentelemetry/api-logs';
import { createLogger } from './logger';
import { runInPageContext, getActivePageRoute } from './router';

export interface MessageContext {
  event?: string;
  page?: string;
  tabId?: string;
  practiceId?: string;
  clientId?: string;
  userProfileId?: string;
  entityId?: string;
  financialYearId?: string;
  supportAccessRequestId?: string;
  hasPayload?: boolean;
  payloadSize?: number;
}

type DropReason = 'stale_nav' | 'max_retries' | 'unhandled';

const log = createLogger('app-message').configure({ level: 'debug' });

// Prefix every bridge log body with the OTEL service name so they're easy to scan/filter.
const serviceName = import.meta.env.VITE_OTEL_SERVICE_NAME || 'dx-vue-web';
const tag = (body: string): string => `[${serviceName}] ${body}`;

// Instruments are created lazily on first use: getMeter() returns a no-op meter
// until the global MeterProvider is registered by instrumentation.ts, and these
// record* functions only ever fire at component runtime (well after startup).
let messageCount: Counter | undefined;
let messageDropped: Counter | undefined;
let initRetries: Counter | undefined;
let handshakeDuration: Histogram | undefined;

const ensureInstruments = (): void => {
  if (messageCount) return;
  const meter = metrics.getMeter('app-message');
  // Create all instruments into locals first so a mid-creation throw leaves all
  // four module-level variables unassigned and the guard retries on the next call.
  const _messageCount = meter.createCounter('app.message.count', {
    description: 'Vue<->Angular bridge messages by direction and event type',
    unit: '1'
  });
  const _messageDropped = meter.createCounter('app.message.dropped', {
    description: 'Bridge messages that did not result in their intended action',
    unit: '1'
  });
  const _initRetries = meter.createCounter('app.message.init.retries', {
    description: 'Init-retry attempts while waiting for the Vue app to finish initialising',
    unit: '1'
  });
  const _handshakeDuration = meter.createHistogram('app.message.init.handshake.duration', {
    description: 'Time from first init sent to init received',
    unit: 'ms'
  });
  // All four created successfully — assign atomically.
  messageCount = _messageCount;
  messageDropped = _messageDropped;
  initRetries = _initRetries;
  handshakeDuration = _handshakeDuration;
};

// Master switch for bridge instrumentation. Opt-in (matches VITE_RESOURCE_ENABLE_OTEL_*):
// when unset/not 'true', no app.message.* metrics or logs are emitted at all. Read per call so
// it can be toggled at runtime (and stubbed in tests).
// _.isEqual (not ===) so the CI env-placeholder build can't constant-fold this comparison:
// with ===, the minifier inlines the single-use call, folds "env:ENABLE_OTEL_TRACKING" === 'true'
// to false at build time, and dead-code-eliminates all bridge telemetry before entrypoint.sh
// ever gets to substitute the real value.
const isEnabled = (value: string | undefined) => _.isEqual(value, 'true');

// Telemetry must never break message processing. Swallow everything.
const safe = (fn: () => void): void => {
  if (!isEnabled(import.meta.env.VITE_RESOURCE_ENABLE_OTEL_TRACKING)) return;
  try {
    ensureInstruments();
    fn();
  } catch {
    /* observability failures must not affect the bridge */
  }
};

// Convert a raw bridge message into log/metric-safe metadata. NEVER copies
// the payload itself — only its presence and a coarse size.
// _direction is unused here — callers pass it for call-site symmetry with
// recordInbound/recordOutbound, which add `direction` to attrs themselves.
const toContext = (message: Record<string, unknown>, _direction: 'inbound' | 'outbound'): MessageContext => {
  // Callers pass toContext(...) as an argument outside the safe() guard, so it must
  // never throw into message processing. Any failure degrades to an empty context.
  try {
    const headers = (message?.headers ?? {}) as Record<string, unknown>;
    const payload = message?.payload;
    let payloadSize = 0;
    try {
      payloadSize = _.isNil(payload) ? 0 : JSON.stringify(payload).length;
    } catch {
      payloadSize = -1; // non-serialisable payload (e.g. circular); size unknown
    }
    return {
      event: message?.event as string | undefined,
      // Many messages don't carry a page (save/reloadClientMenu, and all inbound events other
      // than loadPage/loadTab). Fall back to the current route so bridge logs still name a page.
      // loadPage/loadTab keep their own page (the navigation target); the fallback only fills gaps.
      page: (message?.page as string | undefined) ?? getActivePageRoute(),
      tabId: message?.tabId as string | undefined,
      practiceId: (message?.practiceId ?? headers?.PracticeId) as string | undefined,
      clientId: (message?.clientId ?? headers?.ClientId) as string | undefined,
      userProfileId: message?.userProfileId as string | undefined,
      entityId: (message?.entityId ?? headers?.EntityId) as string | undefined,
      financialYearId: message?.financialYearId as string | undefined,
      supportAccessRequestId: (message?.supportAccessRequestId ?? headers?.SupportAccessRequestId) as
        | string
        | undefined,
      hasPayload: !_.isNil(payload),
      payloadSize
    };
  } catch {
    return {};
  }
};

type MessageAttrs = AnyValueMap & Attributes;

// Full metadata for LOGS — includes high-cardinality fields (tabId, page, payloadSize).
const logAttrs = (extra: Record<string, unknown>, ctx?: MessageContext): MessageAttrs =>
  _.omitBy({ ...(ctx ?? {}), ...extra }, _.isNil) as MessageAttrs;

// LOW-cardinality subset for METRICS — explicit dims + bounded tenancy only.
// Deliberately omits tabId, page, hasPayload, payloadSize, entityId, financialYearId,
// supportAccessRequestId (high/unbounded cardinality). clientId/practiceId enable
// tenant slicing in dashboards.
const metricAttrs = (extra: Record<string, unknown>, ctx?: MessageContext): MessageAttrs =>
  _.omitBy({ ...extra, clientId: ctx?.clientId, practiceId: ctx?.practiceId }, _.isNil) as MessageAttrs;

let handshakeStart: number | undefined;

export const appMessage = {
  toContext,

  recordInbound: (event: string | undefined, ctx?: MessageContext): void => {
    safe(() => {
      const dims = { direction: 'inbound', event: event ?? 'unknown' };
      const on = ctx?.page ? ` on ${ctx.page}` : '';
      runInPageContext(() =>
        log.info(tag(`Received "${event ?? 'unknown'}" message from dx-web-ui${on}`), logAttrs(dims, ctx))
      );
      messageCount!.add(1, metricAttrs(dims, ctx));
    });
  },

  recordOutbound: (event: string | undefined, ctx?: MessageContext): void => {
    safe(() => {
      const dims = { direction: 'outbound', event: event ?? 'unknown' };
      const from = ctx?.page ? ` from ${ctx.page}` : '';
      runInPageContext(() =>
        log.debug(tag(`Sent "${event ?? 'unknown'}" message to dx-web-ui${from}`), logAttrs(dims, ctx))
      );
      messageCount!.add(1, metricAttrs(dims, ctx));
    });
  },

  recordStaleNav: (ctx?: MessageContext): void => {
    safe(() => {
      const dims = { reason: 'stale_nav' as DropReason };
      runInPageContext(() => log.warn(tag(`Skipped a stale navigation message from dx-web-ui`), logAttrs(dims, ctx)));
      messageDropped!.add(1, metricAttrs(dims, ctx));
    });
  },

  recordInitRetry: (attempt: number, max: number): void => {
    safe(() => {
      runInPageContext(() =>
        log.info(
          tag(`Waiting for Vue init to complete - re-requesting init (attempt ${attempt} of ${max})`),
          logAttrs({ attempt, max })
        )
      );
      initRetries!.add(1, { attempt });
    });
  },

  recordInitMaxRetries: (ctx?: MessageContext): void => {
    safe(() => {
      const dims = { reason: 'max_retries' as DropReason };
      runInPageContext(() =>
        log.error(tag(`Gave up waiting for Vue init to complete after maximum retries`), logAttrs(dims, ctx))
      );
      messageDropped!.add(1, metricAttrs(dims, ctx));
    });
  },

  recordUnhandled: (event: string | undefined, ctx?: MessageContext): void => {
    safe(() => {
      const dims = { reason: 'unhandled' as DropReason, event: event ?? 'unknown' };
      runInPageContext(() =>
        log.error(
          tag(`Received an unsupported message type "${event ?? 'unknown'}" from dx-web-ui`),
          logAttrs(dims, ctx)
        )
      );
      messageDropped!.add(1, metricAttrs(dims, ctx));
    });
  },

  recordInitSent: (): void => {
    safe(() => {
      // Only the first un-received init starts the timer, so the histogram measures time-to-first-successful-handshake.
      if (_.isNil(handshakeStart)) handshakeStart = Date.now();
      runInPageContext(() => log.debug(tag(`Requested init handshake from dx-web-ui`)));
    });
  },

  recordInitReceived: (): void => {
    safe(() => {
      runInPageContext(() => log.debug(tag(`Received init handshake from dx-web-ui`)));
      if (!_.isNil(handshakeStart)) {
        handshakeDuration!.record(Date.now() - handshakeStart);
        handshakeStart = undefined;
      }
    });
  }
};
